import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useOrderEvents } from "@/hooks/use-order-events";
import { useOrders } from "@/hooks/use-orders";
import { usePublicMenu } from "@/hooks/use-menu";
import { queryKeys } from "@/lib/query-keys";

export default function OrdersPickupBoard() {
  const { t } = useTranslation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const queryClient = useQueryClient();
  const { data: menuData } = usePublicMenu(orgSlug);
  const { data, error } = useOrders({ status: ["ready"], limit: 100 });

  useOrderEvents(orgSlug ? { type: "board", orgSlug } : { type: "org" }, () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
  });

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-destructive">
        {error.message}
      </div>
    );
  }

  const orders = data?.orders ?? [];

  return (
    <div className="flex min-h-screen flex-col items-center bg-background p-8">
      <header className="mb-12 text-center">
        <h1 className="font-bold font-serif text-5xl tracking-tight">
          {menuData?.organization.name ?? ""}
        </h1>
        <p className="mt-2 text-muted-foreground text-xl">
          {t("boards.pickup.title", {
            defaultValue: "Pedidos listos para retirar",
          })}
        </p>
      </header>

      {orders.length === 0 ? (
        <div className="mt-20 text-muted-foreground text-3xl italic">
          {t("boards.pickup.empty", {
            defaultValue: "No hay pedidos listos",
          })}
        </div>
      ) : (
        <div className="grid w-full max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map((order) => (
            <article
              className="flex flex-col items-center justify-center rounded-3xl border-4 border-primary bg-primary/10 p-8 text-center shadow-xl"
              key={order.id}
            >
              <div className="mb-2 font-bold text-6xl tabular-nums">#{order.dailyNumber}</div>
              {order.contactName ? (
                <div className="mt-2 font-semibold text-2xl">{order.contactName}</div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
